import { Chevron } from "@components/icons"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body16BoldStyles,
  body16MainStyles,
  h3Styles,
  h4Styles,
  h5Styles,
  smallcapsBold13Styles,
  subtextStyles,
} from "@constants/typography"
import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import { withAllOptions } from "@utils/ContentfulRichText"
import { emptyRichText } from "@utils/richText"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import _ from "lodash-es"
import React from "react"
import styled from "styled-components"

const Container = styled.div`
  padding: 0 20px;

  @media ${breakpoints.laptop} {
    padding: 0 calc(1 / 12 * 100vw);
  }

  .MuiPaper-root {
    border: none;
    border-bottom: solid 1px ${colors.dividerLineGray};
    box-shadow: none;
  }

  .MuiAccordion-root {
    ::before {
      display: none;
    }
  }

  .Mui-expanded {
    margin: 0;
  }

  .MuiButtonBase-root {
    padding-left: 20px;
    padding-right: 12px;
  }

  .MuiAccordionSummary-root {
    ${body16BoldStyles}
  }

  .MuiAccordionSummary-content {
    padding-right: 32px;
  }

  .MuiAccordionDetails-root {
    padding: 0 20px;
  }

  .MuiCollapse-wrapperInner {
    padding-bottom: 24px;
  }
`

const Content = styled.div``

const RichText = styled.div`
  ${body16MainStyles}
  color: ${colors.black};

  h3 {
    ${h4Styles}
    margin-top: 48px;
    margin-bottom: 38px;
  }

  h4 {
    ${h5Styles}
    margin-top: 48px;
    font-size: 16px;
    line-height: 24px;
  }

  h6 {
    ${smallcapsBold13Styles}
  }

  a {
    text-decoration: underline;
    color: ${colors.brandOrange};
  }

  p {
    margin-top: 8px;
    margin-bottom: 16px;
  }

  p.finePrint {
    ${subtextStyles}
    margin-top: 8px;
    margin-bottom: 12px;
  }

  ul {
    margin: 0 0 24px;
    padding-left: 18px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  li {
    p {
      margin-bottom: 8px;
    }
  }

  @media ${breakpoints.laptop} {
    h3 {
      ${h4Styles}
      margin-bottom: 56px;
    }

    h4 {
      font-size: 18px;
      line-height: 28px;
    }

    p {
      margin-bottom: 16px;
    }

    p.finePrint {
      ${subtextStyles}
      margin-bottom: 12px;
    }

    ul {
      margin-bottom: 30px;
    }

    li {
      p {
        margin-bottom: 8px;
      }
    }
  }

  @media ${breakpoints.desktop} {
    h3 {
      ${h3Styles}
      margin-bottom: 70px;
    }

    h4 {
      font-size: 28px;
      line-height: 42px;
    }

    p {
      margin-bottom: 16px;
    }

    p.finePrint {
      ${subtextStyles}
      margin-bottom: 12px;
    }

    ul {
      margin-bottom: 30px;
    }

    li {
      p {
        margin-bottom: 8px;
      }
    }
  }
`

const IconWrapper = styled.div`
  flex-direction: row;
  align-items: center;
  display: flex;
  transform: rotate(90deg);
  width: 24px;
  height: 24px;
`

interface FaqProps {
  answer: {
    raw: string
  }
  blockId?: string
  question: string
}

const Faq: React.FC<FaqProps> = ({ answer, blockId, question }) => (
  <Container id={blockId || undefined}>
    <Content>
      <Accordion square>
        <AccordionSummary
          aria-controls={_.snakeCase(question)}
          expandIcon={
            <IconWrapper>
              <Chevron />
            </IconWrapper>
          }
          id={_.snakeCase(question)}
        >
          {question}
        </AccordionSummary>
        <AccordionDetails>
          <RichText>
            {renderRichText(answer || emptyRichText, withAllOptions)}
          </RichText>
        </AccordionDetails>
      </Accordion>
    </Content>
  </Container>
)

export default Faq
